/* eslint-disable prettier/prettier */
import { Box, Typography } from '@mui/material'
import moment from 'moment'
import PropTypes from 'prop-types'
import React, { useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import WhatsAppBG from '../assets/images/whatsapp-bg.jpeg'
import { templatePreviewParts } from '../helpers/whatsappTemplate'
import { BRAND, SURFACE, TEXT } from '../theme/tokens'
import WhatsAppMessagePreview from './WhatsAppMessagePreview'
import ErrorBanner from './shared/ErrorBanner'

// Until the frame has measured its email.
const EMAIL_START_HEIGHT = 600
// An email is laid out for about 600px; a dialog grows to fit it (and stays
// inside a phone's width).
const DIALOG_MIN_WIDTH = 'min(720px, calc(100vw - 64px))'

const HTML_TAG = /<\/?[a-z][\s\S]*?>/i
const escapeHtml = (text) =>
    text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

const NEWLINE_IN_TEXT = /\S[ \t]*\r?\n[ \t]*\S/
const KEEPS_ITS_OWN_BREAKS = ['STYLE', 'SCRIPT', 'PRE', 'TEXTAREA']

/**
 * An email whose text sits in a block with raw newlines (no <br> or <p>), as
 * the builder's paragraph block used to write it. HTML collapses those into
 * one paragraph, so the blocks that hold such text are shown with their line
 * breaks. Blocks with child elements, and markup the email lays out itself,
 * are left alone.
 */
const keepLineBreaks = (html) => {
    const doc = new DOMParser().parseFromString(html, 'text/html')
    let changed = false
    doc.body.querySelectorAll('*').forEach((el) => {
        if (
            el.children.length === 0 &&
            !KEEPS_ITS_OWN_BREAKS.includes(el.tagName) &&
            NEWLINE_IN_TEXT.test(el.textContent)
        ) {
            el.style.whiteSpace = 'pre-line'
            changed = true
        }
    })
    return changed ? `<!doctype html>${doc.documentElement.outerHTML}` : html
}

// Text between table tags, which a parser would move out in front of the table.
const TEXT_AMONG_TABLE_TAGS =
    /<(?:table|tbody|thead|tfoot|tr)\b[^>]*>\s*[^<\s]|<\/(?:td|th|tr|tbody|thead|tfoot)>\s*[^<\s]/i

/**
 * The HTML to store for an uploaded email. Mail clients, like browsers, fold
 * the raw newlines in an email's text into one paragraph (the preview alone
 * cannot fix what recipients get), so the message block of a builder-style
 * email, a <div> that holds only text, gets a <br> for each newline. Wrapped
 * source in <p>, <td> and the like is left alone, as are the document's own
 * doctype and head. Text with no markup is escaped and broken into lines.
 * Markup that a parser would rearrange (text among table tags) is returned
 * untouched.
 */
export const withLineBreaksForSend = (html) => {
    if (!html) return html
    if (!HTML_TAG.test(html))
        return escapeHtml(html).replace(/\r?\n/g, '<br>\n')
    if (TEXT_AMONG_TABLE_TAGS.test(html)) return html
    const doc = new DOMParser().parseFromString(html, 'text/html')
    let changed = false
    doc.body.querySelectorAll('div').forEach((el) => {
        if (el.children.length === 0 && NEWLINE_IN_TEXT.test(el.textContent)) {
            const [, lead, middle, trail] = el.textContent.match(
                /^(\s*)([\s\S]*?)(\s*)$/
            )
            el.innerHTML =
                escapeHtml(lead) +
                escapeHtml(middle).replace(/[ \t]*\r?\n[ \t]*/g, '<br>') +
                escapeHtml(trail)
            changed = true
        }
    })
    if (!changed) return html
    // Keep whatever came before <html> (the original doctype, comments). With
    // no <html> tag the parser files a leading <style>/<meta> under <head>.
    const htmlTag = html.search(/<html[\s>]/i)
    return htmlTag < 0
        ? doc.head.innerHTML + doc.body.innerHTML
        : html.slice(0, htmlTag) + doc.documentElement.outerHTML
}

/**
 * What goes into the frame. An email saved as plain text (no markup) relies on
 * its newlines, which HTML collapses into one paragraph, so it is escaped and
 * shown with its line breaks. An email with markup is shown as it is, except
 * that text with raw newlines keeps them (keepLineBreaks).
 */
export const emailFrameHtml = (html) => {
    if (!html) return html
    if (!HTML_TAG.test(html))
        return `<div style="white-space:pre-wrap;padding:16px">${escapeHtml(
            html
        )}</div>`
    return keepLineBreaks(html)
}

/**
 * An email's HTML in a frame of its own, as tall as the email.
 *
 * The frame keeps the email whole: its <head> styles and <body> attributes
 * (which a sanitised copy inserted into the page lost) and its media queries.
 * Nothing from it reaches the app either (its <style> tags restyled the whole
 * app while the preview was open). `sandbox="allow-same-origin"` without
 * `allow-scripts` runs nothing in the template, and lets the page read the
 * frame's content height once it has loaded, so the email shows at full
 * length with no scroll bar of its own.
 *
 * For a host that is not a dialog: `minWidth` 0 lets the frame take the
 * host's width, and a smaller `startHeight` lets a short email end at its
 * own length (the frame grows to the email, never shrinks below its start).
 * The Template Writer's result card passes both. `title` names the frame
 * for screen readers when a page shows more than one.
 */
export const EmailFrame = ({
    html,
    minWidth = DIALOG_MIN_WIDTH,
    startHeight = EMAIL_START_HEIGHT,
    title = 'Email preview',
}) => {
    const frame = useRef(null)
    const [height, setHeight] = useState(startHeight)
    const fit = () => {
        const doc = frame.current?.contentDocument
        const contentHeight = Math.max(
            doc?.documentElement?.scrollHeight ?? 0,
            doc?.body?.scrollHeight ?? 0
        )
        if (contentHeight) setHeight(contentHeight)
    }
    return (
        <Box
            component="iframe"
            ref={frame}
            title={title}
            sandbox="allow-same-origin"
            srcDoc={emailFrameHtml(html)}
            onLoad={fit}
            sx={{
                display: 'block',
                width: '100%',
                minWidth,
                height,
                border: 0,
                background: SURFACE.emailCanvas,
            }}
        />
    )
}

EmailFrame.propTypes = {
    html: PropTypes.string.isRequired,
    minWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    startHeight: PropTypes.number,
    title: PropTypes.string,
}

/**
 * The previewed template, from the template slice: an email in its own frame
 * (EmailFrame), a WhatsApp message as it shows on a phone (header, media,
 * footer and buttons from GET /comms/v2/template/{id} when that has arrived
 * for this template), and every other channel as plain text, with a
 * Pinpoint template's last-modified time. A preview that failed shows why.
 * Text is rendered as text, never as markup, so it needs no sanitising.
 */
const TemplateRenderer = () => {
    const { templateData, commType, previewError, previewDetails, previewId } =
        useSelector((state) => state.template)
    if (previewError)
        return (
            <Box sx={{ p: 3 }}>
                <ErrorBanner
                    message={`This template cannot be previewed: ${previewError}`}
                />
            </Box>
        )
    if (commType === 'email') return <EmailFrame html={templateData ?? ''} />
    // Details fetched for another template (a late answer) are not shown.
    const details = previewDetails?.id === previewId ? previewDetails : null
    if (commType === 'whatsapp')
        return (
            <Box
                sx={{
                    p: 2,
                    width: 'min(480px, calc(100vw - 64px))',
                    boxSizing: 'border-box',
                }}
            >
                <WhatsAppMessagePreview
                    {...templatePreviewParts(details)}
                    body={templateData ?? ''}
                    emptyBodyText="This template has no body text."
                />
            </Box>
        )
    return (
        <>
            {details?.source === 'pinpoint' && (
                <Typography
                    variant="caption"
                    component="div"
                    sx={{ px: 2, py: 1, color: TEXT.muted }}
                >
                    Managed in Amazon Pinpoint
                    {details.lastModified &&
                        moment(details.lastModified).isValid() &&
                        `. Last modified ${moment(details.lastModified).format(
                            'DD MMM YYYY, HH:mm'
                        )}`}
                </Typography>
            )}
            <Box
                component="pre"
                aria-label="Template text"
                sx={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    background: `url(${WhatsAppBG})`,
                    color: BRAND.deepBlue,
                    p: '15px',
                    m: 0,
                    typography: 'body1',
                    width: '100%',
                    minHeight: '90vh',
                    boxSizing: 'border-box',
                    overflow: 'auto',
                }}
            >
                {templateData}
            </Box>
        </>
    )
}

export default TemplateRenderer
